import { useEffect, useRef, useState, type MouseEvent } from 'react';

/**
 * The ReadyBuy Bottom Line as a tappable 3D ring, from the Property Score
 * handoff. One segment per measure behind the score, sized by its weight, in
 * the brand's ring palette. Tap a segment (or its label) and the ring turns it
 * to face you and the centre shows that measure; tap it again, or the centre,
 * to go back to the overall score.
 *
 * Built from stacked SVG layers in a tilted CSS 3D stage — no WebGL.
 */

export type RingSegment = { label: string; weight: number; result: string; unit: string; note: string };

const PALETTE = ['#D63A2E', '#E22A8C', '#6A2BD9', '#3A3BD6', '#2BB3E6'];
const S = 300;
const C = 150;
const RO = 140;
const RI = 118;
const LAYERS = 12;
const TILT = 62;
const SCALE = 0.72;
const PERSPECTIVE = 900;
const LABEL_R = RO + 62;

const pt = (r: number, deg: number): [number, number] => {
  const t = ((deg - 90) * Math.PI) / 180;
  return [C + r * Math.cos(t), C + r * Math.sin(t)];
};
const arc = (a0: number, a1: number) => {
  const lg = a1 - a0 > 180 ? 1 : 0;
  const [x0, y0] = pt(RO, a0);
  const [x1, y1] = pt(RO, a1);
  const [x2, y2] = pt(RI, a1);
  const [x3, y3] = pt(RI, a0);
  return `M${x0} ${y0} A${RO} ${RO} 0 ${lg} 1 ${x1} ${y1} L${x2} ${y2} A${RI} ${RI} 0 ${lg} 0 ${x3} ${y3}Z`;
};
const shade = (hex: string, k: number) => {
  const n = parseInt(hex.slice(1), 16);
  const f = (v: number) => Math.round(v * k);
  return `rgb(${f(n >> 16)},${f((n >> 8) & 255)},${f(n & 255)})`;
};

function geometry(segs: RingSegment[]) {
  const total = segs.reduce((a, s) => a + s.weight, 0) || 1;
  const gap = 0.6;
  let a = 0;
  return segs.map((s, i) => {
    const sweep = (s.weight / total) * 360;
    const a0 = a + gap / 2;
    const a1 = a + sweep - gap / 2;
    a += sweep;
    return { ...s, i, a0, a1, mid: (a0 + a1) / 2, color: PALETTE[i % PALETTE.length] };
  });
}

const reducedMotion = () => {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
};

/**
 * Lays out in normal flow — the score and its summary, then the ring beneath —
 * so a long summary pushes the ring down instead of running into it. `onPick` reports whether a segment is selected so the
 * parent can swap its hint line.
 */
export function BottomLineRing({
  score,
  unit,
  label = 'READYBUY BOTTOM LINE',
  summary,
  scoreColour,
  segments,
  onPick,
}: {
  score: string;
  unit: string;
  /** The ring centre's heading while nothing is picked, e.g. with "· WITHOUT PRICE". */
  label?: string;
  /** Why the word, under it while nothing is picked. */
  summary?: string;
  /** The overall score's colour (by range). */
  scoreColour?: string;
  segments: RingSegment[];
  onPick?: (picked: boolean) => void;
}) {
  const still = reducedMotion();
  const [drawn, setDrawn] = useState(still);
  const [sel, setSel] = useState(0);
  const [picked, setPicked] = useState(false);
  const [rot, setRot] = useState(0);
  const inner = useRef<HTMLDivElement>(null);
  const g = geometry(segments);

  useEffect(() => {
    if (still) return;
    const t = setTimeout(() => setDrawn(true), 80);
    return () => clearTimeout(t);
  }, [still]);
  useEffect(() => onPick?.(picked), [picked, onPick]);

  const focusOn = (i: number) => {
    // Turn the shortest way round so the segment faces the viewer at 6 o'clock.
    let target = 180 - g[i].mid;
    while (target - rot > 180) target -= 360;
    while (target - rot < -180) target += 360;
    const off = picked && sel === i;
    setPicked(!off);
    setSel(i);
    if (!off) setRot(target);
  };

  // Map a tap back onto the tilted ring: undo the tilt, then the rotation.
  const onTap = (ev: MouseEvent<HTMLDivElement>) => {
    const lbl = (ev.target as HTMLElement).closest?.('[data-seg]');
    if (lbl) {
      focusOn(Number(lbl.getAttribute('data-seg')));
      return;
    }
    if (!inner.current || !g.length) return;
    const r = inner.current.getBoundingClientRect();
    const cx = r.left + r.width / 2;
    const cy = r.top + r.height / 2;
    const dx = ev.clientX - cx;
    const dy = (ev.clientY - cy) / Math.cos((TILT * Math.PI) / 180);
    const rad = Math.hypot(dx, dy) / (r.width / 2);
    if (rad < 0.5) {
      setPicked(false);
      return;
    }
    if (rad > 1.35) return;
    const deg = (Math.atan2(dx, -dy) * 180) / Math.PI;
    const local = (((deg - rot) % 360) + 360) % 360;
    const hit =
      g.find((s) => local >= s.a0 - 1.2 && local <= s.a1 + 1.2) ??
      g.reduce((b, s) => (Math.abs(s.mid - local) < Math.abs(b.mid - local) ? s : b), g[0]);
    focusOn(hit.i);
  };

  const s = g[sel] ?? g[0];
  const tiltR = (TILT * Math.PI) / 180;
  const ease = still ? 'none' : 'transform 1s cubic-bezier(.2,.8,.2,1)';

  return (
    <div style={{ position: 'relative', display: 'flex', flexDirection: 'column' }}>
      {/* Centre: the overall score, or the picked measure */}
      <div
        aria-live="polite"
        style={{
          position: 'relative',
          padding: '0 0 4px',
          zIndex: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'flex-start',
          gap: 12,
          pointerEvents: 'none',
          textAlign: 'center',
        }}
      >
        <div style={{ fontSize: 10, letterSpacing: '0.2em', fontWeight: 600, color: picked && s ? s.color : '#D63A2E' }}>
          {picked && s ? s.label.toUpperCase() : label}
        </div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, padding: '2px 0 4px' }}>
          <span
            style={{
              fontStretch: '125%',
              fontSize: picked && s ? (s.result.length > 9 ? 30 : s.result.length > 5 ? 38 : 48) : score.length > 9 ? 34 : score.length > 5 ? 42 : 56,
              fontWeight: 800,
              letterSpacing: '-0.04em',
              lineHeight: 0.9,
              color: picked && s ? s.color : scoreColour ?? '#1F1A1F',
              whiteSpace: 'nowrap',
            }}
          >
            {picked && s ? s.result : score}
          </span>
          {(picked && s ? s.unit : unit) && (
            <span style={{ fontSize: 13, fontWeight: 600, color: 'rgba(31,26,31,0.45)' }}>{picked && s ? s.unit : unit}</span>
          )}
        </div>
        {picked && s ? (
          <div style={{ fontSize: 11, letterSpacing: '0.04em', fontWeight: 600, color: 'rgba(31,26,31,0.6)', lineHeight: 1.6, maxWidth: 240, textWrap: 'balance' }}>
            {s.note}
          </div>
        ) : (
          summary && (
            <div style={{ fontSize: 12, fontWeight: 500, color: 'rgba(31,26,31,0.7)', lineHeight: 1.5, maxWidth: 290, padding: '0 20px', textWrap: 'pretty' }}>{summary}</div>
          )
        )}
      </div>

      {/* The ring */}
      <div
        className="rb-ring"
        onClick={onTap}
        role="group"
        aria-label="The measures behind the bottom line"
        // The tilted ring leaves the top of its box empty, so it tucks up under the text.
        style={{ position: 'relative', height: S, marginTop: -40, perspective: `${PERSPECTIVE}px`, perspectiveOrigin: '50% 20%', cursor: 'pointer', zIndex: 1 }}
      >
        <div
          ref={inner}
          style={{
            position: 'absolute',
            left: '50%',
            top: 0,
            width: S,
            height: S,
            marginLeft: -S / 2,
            transformStyle: 'preserve-3d',
            transform: `translateY(10px) rotateX(${TILT}deg) scale(${SCALE}) rotateZ(${rot}deg)`,
            transition: ease,
          }}
        >
          <div
            style={{
              position: 'absolute',
              inset: 34,
              borderRadius: '50%',
              background: 'radial-gradient(closest-side, rgba(31,26,31,0.22), rgba(31,26,31,0) 70%)',
              filter: 'blur(10px)',
              transform: 'translateZ(-30px)',
            }}
          />
          <svg
            width={S}
            height={S}
            viewBox={`0 0 ${S} ${S}`}
            style={{ position: 'absolute', inset: 0, overflow: 'visible', pointerEvents: 'none', opacity: drawn ? 1 : 0, transition: still ? 'none' : 'opacity .8s .4s' }}
          >
            <circle cx={C} cy={C} r={RO + 22} fill="none" stroke="rgba(31,26,31,0.16)" strokeWidth={0.8} />
            <circle cx={C} cy={C} r={RO + 40} fill="none" stroke="rgba(31,26,31,0.1)" strokeWidth={0.8} strokeDasharray="1 5" />
            <circle cx={C} cy={C} r={RI - 16} fill="none" stroke="rgba(31,26,31,0.12)" strokeWidth={0.8} strokeDasharray="2 4" />
            {Array.from({ length: 60 }, (_, t) => {
              const big = t % 5 === 0;
              const [x1, y1] = pt(RO + 16, t * 6);
              const [x2, y2] = pt(RO + (big ? 28 : 22), t * 6);
              return <line key={t} x1={x1} y1={y1} x2={x2} y2={y2} stroke={big ? 'rgba(31,26,31,0.35)' : 'rgba(31,26,31,0.18)'} strokeWidth={big ? 1 : 0.7} />;
            })}
          </svg>
          {g.flatMap((seg) => {
            const isSel = picked && seg.i === sel;
            const dim = picked && !isSel;
            const delay = drawn && !still ? `${seg.i * 0.07}s` : '0s';
            const next = g[(seg.i + 1) % g.length].color;
            const out = [
              <svg
                key={`glow-${seg.i}`}
                width={S}
                height={S}
                viewBox={`0 0 ${S} ${S}`}
                style={{
                  position: 'absolute',
                  inset: 0,
                  overflow: 'visible',
                  transform: 'translateZ(-6px)',
                  filter: 'blur(9px)',
                  opacity: drawn ? (dim ? 0.12 : 0.32) : 0,
                  transition: still ? 'none' : 'opacity .8s',
                  pointerEvents: 'none',
                }}
              >
                <path d={arc(seg.a0 - 1, seg.a1 + 1)} fill={seg.color} />
              </svg>,
            ];
            for (let k = 0; k < LAYERS; k++) {
              const top = k === LAYERS - 1;
              out.push(
                <svg
                  key={`${seg.i}-${k}`}
                  width={S}
                  height={S}
                  viewBox={`0 0 ${S} ${S}`}
                  style={{
                    position: 'absolute',
                    inset: 0,
                    overflow: 'visible',
                    pointerEvents: 'none',
                    transform: `translateZ(${k * 1.8 + (isSel ? 16 : 0)}px) scale(${drawn ? (isSel ? 1.06 : 1) : 0.7})`,
                    opacity: (drawn ? 1 : 0) * (dim ? 0.35 : 1),
                    transition: still ? 'none' : `transform .9s cubic-bezier(.2,.8,.2,1) ${delay}, opacity .6s ${delay}`,
                  }}
                >
                  {top && (
                    <defs>
                      <linearGradient id={`rbg${seg.i}`} gradientUnits="userSpaceOnUse" x1={pt(RO, seg.a0)[0]} y1={pt(RO, seg.a0)[1]} x2={pt(RO, seg.a1)[0]} y2={pt(RO, seg.a1)[1]}>
                        <stop offset="0%" stopColor={seg.color} />
                        <stop offset="100%" stopColor={next} />
                      </linearGradient>
                      <radialGradient id={`rbr${seg.i}`} cx={C} cy={C} r={RO} gradientUnits="userSpaceOnUse">
                        <stop offset={`${((RI / RO) * 100).toFixed(0)}%`} stopColor="rgba(0,0,0,0.35)" />
                        <stop offset={`${(((RI + 8) / RO) * 100).toFixed(0)}%`} stopColor="rgba(255,255,255,0)" />
                        <stop offset="88%" stopColor="rgba(255,255,255,0.05)" />
                        <stop offset="97%" stopColor="rgba(255,255,255,0.45)" />
                        <stop offset="100%" stopColor="rgba(255,255,255,0.1)" />
                      </radialGradient>
                      <linearGradient id={`rbs${seg.i}`} x1={0} y1={0} x2={0} y2={1}>
                        <stop offset="0%" stopColor="rgba(255,255,255,0.55)" />
                        <stop offset="45%" stopColor="rgba(255,255,255,0)" />
                      </linearGradient>
                    </defs>
                  )}
                  <path
                    d={arc(seg.a0, seg.a1)}
                    fill={top ? `url(#rbg${seg.i})` : shade(seg.color, 0.55 + k * 0.035)}
                    stroke={top && isSel ? 'rgba(255,255,255,0.9)' : 'none'}
                    strokeWidth={top && isSel ? 1.2 : 0}
                  />
                  {top && <path d={arc(seg.a0, seg.a1)} fill={`url(#rbr${seg.i})`} />}
                  {top && <path d={arc(seg.a0, seg.a1)} fill={`url(#rbs${seg.i})`} style={{ mixBlendMode: 'screen' }} />}
                </svg>,
              );
            }
            return out;
          })}
        </div>

        {/* Labels, projected outside the ring and kept inside the frame */}
        {g.map((seg) => {
          const a = ((seg.mid + rot) * Math.PI) / 180;
          const x0 = LABEL_R * Math.sin(a) * SCALE;
          const y0 = -LABEL_R * Math.cos(a) * SCALE;
          const zz = y0 * Math.sin(tiltR);
          const p = PERSPECTIVE / (PERSPECTIVE - zz);
          const w = Math.max(seg.label.length * 6.4, 28) + 22;
          const dx = Math.max(-(195 - 12 - w / 2), Math.min(195 - 12 - w / 2, x0 * p));
          const y = 160 + y0 * Math.cos(tiltR) * p + (zz < 0 ? -18 : 12);
          const on = picked && seg.i === sel;
          return (
            <button
              type="button"
              key={seg.label}
              data-seg={seg.i}
              aria-pressed={on}
              aria-label={`${seg.label}: ${seg.result}${seg.unit}`}
              style={{
                position: 'absolute',
                left: `calc(50% + ${dx}px)`,
                top: y,
                transform: 'translate(-50%,-50%)',
                zIndex: zz > 0 ? 6 : 4,
                transition: still ? 'none' : 'left 1s cubic-bezier(.2,.8,.2,1), top 1s cubic-bezier(.2,.8,.2,1), opacity .5s',
                opacity: drawn ? (picked && !on ? 0.55 : 1) : 0,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '2px 0',
                whiteSpace: 'nowrap',
                textAlign: 'left',
              }}
            >
              <span
                style={{
                  width: on ? 9 : 6,
                  height: on ? 9 : 6,
                  borderRadius: '50%',
                  background: seg.color,
                  boxShadow: on ? `0 0 0 4px ${seg.color}33, 0 0 10px ${seg.color}` : 'none',
                  transition: 'all .3s',
                  flex: 'none',
                }}
              />
              <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.14em', color: on ? seg.color : '#1F1A1F' }}>{seg.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
